<script setup>
import {onMounted, ref} from 'vue';
import {useRouter, useRoute} from 'vue-router';
import {definePage} from "unplugin-vue-router/runtime";
import logoWhite from "@/assets/logo_full_white.svg"
import eyeIcon from "@/assets/eye.svg";
import eyeSlashIcon from "@/assets/eye-slash.svg";
import createApiService from '@/services/api';
import questionWhiteIcon from '@/assets/question-circle.svg'

const router = useRouter();
const route = useRoute();
const api = createApiService(router);

import {useMessageStore} from "@/stores/message.js";

const messageStore = useMessageStore();

definePage({
  meta: {
    title: 'Registreren',
    requiresAuth: false,
  },
})

const loading = ref(false);
const firstName = ref(null);
const insertion = ref(null);
const lastName = ref(null);
const email = ref(null);
const password = ref(null);
const passwordRepeat = ref(null);
const showPassword = ref(false);

const addMantelzorger = ref(false);
const mantelzorgerFirstName = ref(null)
const mantelzorgerInsertion = ref(null)
const mantelzorgerLastName = ref(null)
const mantelzorgerEmail = ref(null)

const agreement = ref(false);

const message = ref('');
const errors = ref([]);

const initialLoadComplete = ref(false);

const submitRegistration = async () => {
  loading.value = true;
  resetErrors();

  window.scrollTo({top: 0, left: 0, behavior: 'smooth'});

  if (!agreement.value) { // must agree to terms
    errors.value = ['U dient akkoord te gaan met de voorwaarden.'];
    loading.value = false;
    return;
  }

  try {
    const response = await api.post('register', {
      firstName: firstName.value,
      insertion: insertion.value,
      lastName: lastName.value,
      email: email.value,
      password: password.value,
      passwordRepeat: passwordRepeat.value,
      addMantelzorger: addMantelzorger.value,
      mantelzorgerFirstName: mantelzorgerFirstName.value,
      mantelzorgerInsertion: mantelzorgerInsertion.value,
      mantelzorgerLastName: mantelzorgerLastName.value,
      mantelzorgerEmail: mantelzorgerEmail.value,
    });

    if (response.status === 200) {
      messageStore.addMessage(response.data.message);
      await router.push('/inloggen');
    }
    else {
      errors.value = ['Er is iets misgegaan. Probeer het later nog eens.'];
    }
  } catch (e) {
    if (e.response?.status === 400) {
      for (const value of Object.values(e.response.data.data)) {
        errors.value.push(value);
      }
    }
    else {
      console.error(e);
      errors.value = ['Er is iets misgegaan. Probeer het later nog eens.'];
    }
  } finally {
    loading.value = false;
  }
}

const resetErrors = () => {
  errors.value = [];
}

onMounted(async () => {
  initialLoadComplete.value = true;
});
</script>

<template>
  <the-loader v-if="loading"/>

  <v-card
    class="ma-auto pa-5"
    color="primary"
    :disabled="loading"
    max-width="500"
  >

    <v-img :src="logoWhite" max-width="50%" class="mx-auto mb-12"></v-img>

    <v-form
      v-if="initialLoadComplete"
      @submit.prevent="submitRegistration"
    >
      <v-card-text class="pl-0 pr-0">
        <v-alert
          v-if="message.length"
          class="mx-auto mb-5"
          max-width="500"
          color="success"
          variant="elevated"
        >
          {{ message }}
        </v-alert>

        <v-alert
          v-if="errors.length"
          class="mx-auto mb-5"
          max-width="500"
          color="error"
          variant="elevated"
        >
          <template #text>
            <ul id="error-list">
              <li
                v-for="(error, index) in errors"
                :key="index"
              >
                {{ error }}
              </li>
            </ul>
          </template>
        </v-alert>

        <div class="mb-4">
          <h4 class="mb-2">Voornaam <span class="text-error">*</span></h4>
          <v-text-field
            v-model="firstName"
            placeholder="Naam"
            variant="solo"
            hide-details
          />
        </div>

        <div class="mb-4">
          <h4 class="mb-2">Tussenvoegsel</h4>
          <v-text-field
            v-model="insertion"
            placeholder="Tussenvoegsel"
            variant="solo"
            hide-details
          />
        </div>

        <div class="mb-4">
          <h4 class="mb-2">Achternaam <span class="text-error">*</span></h4>
          <v-text-field
            v-model="lastName"
            placeholder="Achternaam"
            variant="solo"
            hide-details
          />
        </div>

        <div class="mb-4">
          <h4 class="mb-2">E-mailadres <span class="text-error">*</span></h4>
          <v-text-field
            v-model="email"
            placeholder="E-mailadres"
            variant="solo"
            hide-details
          />
        </div>

        <div class="mb-4">
          <h4 class="mb-2">Wachtwoord <span class="text-error">*</span></h4>
          <v-text-field
            v-model="password"
            placeholder="Wachtwoord"
            variant="solo"
            :type="showPassword ? 'text' : 'password'"
            hide-details
          >
            <template #append-inner>
              <v-icon
                :title="showPassword ? 'Verbergen' : 'Tonen'"
                @click="showPassword = !showPassword"
                class="cursor-pointer opacity-100"
              >
                <v-img :src="showPassword ? eyeIcon : eyeSlashIcon"></v-img>
              </v-icon>
            </template>
          </v-text-field>
        </div>

        <div class="mb-4">
          <h4 class="mb-2">Herhaal wachtwoord <span class="text-error">*</span></h4>
          <v-text-field
            v-model="passwordRepeat"
            placeholder="Herhaal wachtwoord"
            variant="solo"
            :type="showPassword ? 'text' : 'password'"
            hide-details
          >
            <template #append-inner>
              <v-icon
                :title="showPassword ? 'Verbergen' : 'Tonen'"
                @click="showPassword = !showPassword"
                class="cursor-pointer opacity-100"
              >
                <v-img :src="showPassword ? eyeIcon : eyeSlashIcon"></v-img>
              </v-icon>
            </template>
          </v-text-field>
        </div>

        <div class="mb-4">
          <v-switch
            v-model="addMantelzorger"
            color="secondary"
            base-color="white"
            hide-details
          >
            <template #label>
              Mantelzorger toevoegen
              <v-tooltip
                max-width="500"
                text="Vink deze optie aan om een registratie aan te vragen voor een mantelzorger. Zij zullen per mail een link ontvangen om een wachtwoord in te stellen."
                location="bottom"
              >
                <template #activator="{props}">
                  <v-img v-bind="props" class="ml-1 cursor-help" :src="questionWhiteIcon" width="24"
                         height="24"/>
                </template>
              </v-tooltip>
            </template>
          </v-switch>

          <template v-if="addMantelzorger">
            <p class="text-center mb-4">Gegevens mantelzorger</p>
            <div class="mb-4">
              <h4 class="mb-2">Voornaam <span class="text-error">*</span></h4>
              <v-text-field
                v-model="mantelzorgerFirstName"
                placeholder="Voornaam"
                variant="solo"
                hide-details
              />
            </div>
            <div class="mb-4">
              <h4 class="mb-2">Tussenvoegsel</h4>
              <v-text-field
                v-model="mantelzorgerInsertion"
                placeholder="Tussenvoegsel"
                variant="solo"
                hide-details
              />
            </div>

            <div class="mb-4">
              <h4 class="mb-2">Achternaam <span class="text-error">*</span></h4>
              <v-text-field
                v-model="mantelzorgerLastName"
                placeholder="Achternaam"
                variant="solo"
                hide-details
              />
            </div>
            <div class="mb-4">
              <h4 class="mb-2">E-mailadres <span class="text-error">*</span></h4>
              <v-text-field
                v-model="mantelzorgerEmail"
                placeholder="E-mailadres"
                variant="solo"
                hide-details
              />
            </div>
          </template>
        </div>

        <v-checkbox
          v-model="agreement"
          height="24"
          color="secondary"
        >
          <template #label>
            <p class="p-small">
              Ik ga akkoord met de <a class="text-white font-weight-bold" href="/voorwaarden">voorwaarden</a>
            </p>
          </template>
        </v-checkbox>

      </v-card-text>

      <v-card-actions class="flex-column ma-auto pa-0 mt-2 actions">
        <v-btn
          class="mb-2 w-100"
          height="54"
          type="submit"
          color="secondary"
          active-color="primary"
          variant="elevated"
        >
          <h4>Registreren</h4>
        </v-btn>

        <a href="/wachtwoord-vergeten" class="text-white">
          Wachtwoord vergeten?
        </a>
      </v-card-actions>
    </v-form>

    <div
      v-else
      class="text-center py-10"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>

    <div class="bottom-link text-center w-100 pb-4 mt-8">
      <p class="p-small">Heb je al een account?</p>
      <a href="/inloggen" class="text-white">
        Log in
      </a>
    </div>
  </v-card>


</template>

<style scoped>
.actions {
  min-height: auto;
}
</style>
